import React, { useEffect, useState } from 'react';
import { CalendarIcon, MapPinIcon, PhoneIcon, MailIcon } from 'lucide-react';
import emailjs from '@emailjs/browser';
export const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventDate: '',
    startTime: '',
    endTime: '',
    bookingType: '',
    package: '',
    partyLocation: '',
    partyCity: '',
    otherLocationDetails: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    success?: boolean;
    message?: string;
  }>({});
  // Package options for each booking type
  const softPlayPackages = ['Tiny Tot', 'Mega Tot', 'Gigantatot', 'Fancy Tot', 'Mega Fancy Tot'];
  const bounceHousePackages = ['Small Bounce House', 'Medium Bounce House', 'Combo Bounce House', 'Medium White Bounce House', 'Large Combo White Bounce House'];
  const photoboothPackages = ['Self-serve Photobooth'];
  // State to hold current package options
  const [packageOptions, setPackageOptions] = useState<string[]>([]);
  // Update package options when booking type changes
  useEffect(() => {
    if (formData.bookingType === 'softplay') {
      setPackageOptions(softPlayPackages);
    } else if (formData.bookingType === 'bouncehouse') {
      setPackageOptions(bounceHousePackages);
    } else if (formData.bookingType === 'photobooth') {
      setPackageOptions(photoboothPackages);
    } else {
      setPackageOptions([]);
    }
    // Reset package selection when booking type changes
    setFormData(prev => ({
      ...prev,
      package: ''
    }));
  }, [formData.bookingType]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({});
    try {
      // EmailJS configuration
      const serviceId = 'service_wywqkye'; // Your EmailJS service ID
      const templateId = 'template_6d30pxw'; // Your EmailJS template ID
      const publicKey = 'nKRg8BnYcuXf4azVI'; // Your provided public key
      // Format the data to be more readable in the email
      const formattedData = {
        ...formData,
        to_email: 'toddlertimeevents@gmail.com',
        // Get the display name of the package from the value
        package_display: packageOptions.find(pkg => pkg.toLowerCase().replace(/\s+/g, '-') === formData.package) || formData.package,
        // Format date and time for better readability
        formatted_date: formData.eventDate ? new Date(formData.eventDate).toLocaleDateString() : '',
        event_time: `${formData.startTime} to ${formData.endTime}`
      };
      const result = await emailjs.send(serviceId, templateId, formattedData, publicKey);
      if (result.status === 200) {
        setSubmitStatus({
          success: true,
          message: 'Thank you for your inquiry! We will contact you shortly.'
        });
        // Reset form after successful submission
        setFormData({
          name: '',
          email: '',
          phone: '',
          eventDate: '',
          startTime: '',
          endTime: '',
          bookingType: '',
          package: '',
          partyLocation: '',
          partyCity: '',
          otherLocationDetails: '',
          message: ''
        });
      } else {
        throw new Error('Failed to send email');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      setSubmitStatus({
        success: false,
        message: 'There was a problem sending your inquiry. Please try again or contact us directly at info@toddlertimeatl.com.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  return <div className="bg-white w-full" data-id="element-375">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20" data-id="element-376">
        <div className="text-center mb-12" data-id="element-377">
          <h1 className="text-4xl md:text-5xl font-bold text-primary-500 leading-tight" data-id="element-378">
            Contact Us
          </h1>
          <p className="mt-4 text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-379">
            Let's create a magical play experience for your little ones!
          </p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-380">
          <div className="bg-white rounded-lg shadow-lg p-6 lg:col-span-1 border border-gray-100" data-id="element-381">
            <h3 className="text-xl font-semibold text-gray-900 mb-4" data-id="element-382">
              Get in Touch
            </h3>
            <div className="space-y-4" data-id="element-383">
              <div className="flex items-start" data-id="element-384">
                <div className="flex-shrink-0" data-id="element-385">
                  <PhoneIcon className="h-6 w-6 text-primary-500" data-id="element-386" />
                </div>
                <div className="ml-3 text-base text-gray-500" data-id="element-387">
                  <p data-id="element-388">‪(470) 243-0414‬</p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-389">
                <div className="flex-shrink-0" data-id="element-390">
                  <MailIcon className="h-6 w-6 text-primary-500" data-id="element-391" />
                </div>
                <div className="ml-3 text-base text-gray-500" data-id="element-392">
                  <p data-id="element-393">info@toddlertimeatl.com</p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-394">
                <div className="flex-shrink-0" data-id="element-395">
                  <MapPinIcon className="h-6 w-6 text-primary-500" data-id="element-396" />
                </div>
                <div className="ml-3 text-base text-gray-500" data-id="element-397">
                  <p data-id="element-398">Serving the Greater Atlanta Area</p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-399">
                <div className="flex-shrink-0" data-id="element-400">
                  <CalendarIcon className="h-6 w-6 text-primary-500" data-id="element-401" />
                </div>
                <div className="ml-3 text-base text-gray-500" data-id="element-402">
                  <p data-id="element-403">Available 7 days a week</p>
                  <p data-id="element-404">Weekend booking hours: 9am - 7:30pm</p>
                  <p data-id="element-405">Contact us for weekday event inquiries</p>
                </div>
              </div>
            </div>
            <div className="mt-8" data-id="element-406">
              <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider" data-id="element-407">
                Follow Us
              </h4>
              <div className="mt-4 flex space-x-6" data-id="element-408">
                <a href="https://www.facebook.com/toddlertimeatl" className="text-gray-400 hover:text-primary-500" data-id="element-409">
                  <span className="sr-only" data-id="element-410">Facebook</span>
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true" data-id="element-411">
                    <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" data-id="element-412" />
                  </svg>
                </a>
                <a href="https://www.instagram.com/toddlertime.softplay?utm_source=ig_web_button_share_sheet&igsh=MWZoOXp3NzVocGg1cg==" className="text-gray-400 hover:text-primary-500" data-id="element-413">
                  <span className="sr-only" data-id="element-414">Instagram</span>
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true" data-id="element-415">
                    <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" data-id="element-416" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-lg p-6 lg:col-span-2 border border-gray-100" data-id="element-417">
            <h3 className="text-xl font-semibold text-gray-900 mb-4" data-id="element-418">
              Book Your Play Experience
            </h3>
            {/* Status message display */}
            {submitStatus.message && <div className={`mb-6 p-4 rounded-md ${submitStatus.success ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`} data-id="element-419">
                {submitStatus.message}
              </div>}
            <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-8" data-id="element-420">
              <div data-id="element-421">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-422">
                  Full name
                </label>
                <div className="mt-1" data-id="element-423">
                  <input type="text" name="name" id="name" value={formData.name} onChange={handleChange} required className="py-3 px-4 block w-full shadow-sm focus:ring-primary-500 focus:border-primary-500 border-gray-300 rounded-md" data-id="element-424" />
                </div>
              </div>
              <div data-id="element-425">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700" data-id="element-426">
                  Email
                </label>
                <div className="mt-1" data-id="element-427">
                  <input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required className="py-3 px-4 block w-full shadow-sm focus:ring-primary-500 focus:border-primary-500 border-gray-300 rounded-md" data-id="element-428" />
                </div>
              </div>
              <div data-id="element-429">
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700" data-id="element-430">
                  Phone
                </label>
                <div className="mt-1" data-id="element-431">
                  <input type="tel" name="phone" id="phone" value={formData.phone} onChange={handleChange} required className="py-3 px-4 block w-full shadow-sm focus:ring-primary-500 focus:border-primary-500 border-gray-300 rounded-md" data-id="element-432" />
                </div>
              </div>
              <div data-id="element-433">
                <label htmlFor="eventDate" className="block text-sm font-medium text-gray-700" data-id="element-434">
                  Event Date
                </label>
                <div className="mt-1" data-id="element-435">
                  <input type="date" name="eventDate" id="eventDate" value={formData.eventDate} onChange={handleChange} required className="py-3 px-4 block w-full shadow-sm focus:ring-primary-500 focus:border-primary-500 border-gray-300 rounded-md" data-id="element-436" />
                </div>
              </div>
              <div data-id="element-437">
                <label htmlFor="startTime" className="block text-sm font-medium text-gray-700" data-id="element-438">
                  Start Time
                </label>
                <div className="mt-1" data-id="element-439">
                  <input type="text" name="startTime" id="startTime" value={formData.startTime} onChange={handleChange} placeholder="e.g. 2:00 PM" required className="py-3 px-4 block w-full shadow-sm focus:ring-primary-500 focus:border-primary-500 border-gray-300 rounded-md" data-id="element-440" />
                  <p className="mt-1 text-xs text-gray-500" data-id="element-441">
                    Please enter time in format like "2:00 PM" or "10:30 AM"
                  </p>
                </div>
              </div>
              <div data-id="element-442">
                <label htmlFor="endTime" className="block text-sm font-medium text-gray-700" data-id="element-443">
                  End Time
                </label>
                <div className="mt-1" data-id="element-444">
                  <input type="text" name="endTime" id="endTime" value={formData.endTime} onChange={handleChange} placeholder="e.g. 4:00 PM" required className="py-3 px-4 block w-full shadow-sm focus:ring-primary-500 focus:border-primary-500 border-gray-300 rounded-md" data-id="element-445" />
                  <p className="mt-1 text-xs text-gray-500" data-id="element-446">
                    Please enter time in format like "4:00 PM" or "12:30 PM"
                  </p>
                </div>
              </div>
              <div data-id="element-447">
                <label htmlFor="bookingType" className="block text-sm font-medium text-gray-700" data-id="element-448">
                  What are you booking?
                </label>
                <div className="mt-1" data-id="element-449">
                  <select id="bookingType" name="bookingType" value={formData.bookingType} onChange={handleChange} required className="py-3 px-4 block w-full shadow-sm focus:ring-primary-500 focus:border-primary-500 border-gray-300 rounded-md" data-id="element-450">
                    <option value="" data-id="element-451">Select what you're booking</option>
                    <option value="softplay" data-id="element-452">Soft Play Rental</option>
                    <option value="bouncehouse" data-id="element-453">Bounce House Rental</option>
                    <option value="photobooth" data-id="element-454">Photobooth Rental</option>
                  </select>
                </div>
              </div>
              <div data-id="element-455">
                <label htmlFor="package" className="block text-sm font-medium text-gray-700" data-id="element-456">
                  Preferred Package
                </label>
                <div className="mt-1" data-id="element-457">
                  <select id="package" name="package" value={formData.package} onChange={handleChange} disabled={packageOptions.length === 0} required className="py-3 px-4 block w-full shadow-sm focus:ring-primary-500 focus:border-primary-500 border-gray-300 rounded-md disabled:bg-gray-100 disabled:text-gray-500" data-id="element-458">
                    <option value="" data-id="element-459">
                      {packageOptions.length === 0 ? "Please select what you're booking first" : 'Select package'}
                    </option>
                    {packageOptions.map(option => <option key={option} value={option.toLowerCase().replace(/\s+/g, '-')} data-id="element-460">
                        {option}
                      </option>)}
                  </select>
                </div>
              </div>
              <div data-id="element-461">
                <label htmlFor="partyLocation" className="block text-sm font-medium text-gray-700" data-id="element-462">
                  Event location type
                </label>
                <div className="mt-1" data-id="element-463">
                  <select id="partyLocation" name="partyLocation" value={formData.partyLocation} onChange={handleChange} required className="py-3 px-4 block w-full shadow-sm focus:ring-primary-500 focus:border-primary-500 border-gray-300 rounded-md" data-id="element-464">
                    <option value="" data-id="element-465">Select location</option>
                    <option value="home-outside" data-id="element-466">Home (Outside)</option>
                    <option value="home-inside" data-id="element-467">Home (Inside)</option>
                    <option value="event-space" data-id="element-468">Event Space</option>
                    <option value="community-center" data-id="element-469">Community Center</option>
                    <option value="public-park" data-id="element-470">Public Park or Pavilion</option>
                    <option value="other" data-id="element-471">Other</option>
                  </select>
                </div>
              </div>
              <div data-id="element-472">
                <label htmlFor="partyCity" className="block text-sm font-medium text-gray-700" data-id="element-473">
                  Address where the party will be held
                </label>
                <div className="mt-1" data-id="element-474">
                  <input type="text" name="partyCity" id="partyCity" value={formData.partyCity} onChange={handleChange} required placeholder="e.g. 123 Toddler Time St, Douglasville GA 30135" className="py-3 px-4 block w-full shadow-sm focus:ring-primary-500 focus:border-primary-500 border-gray-300 rounded-md" data-id="element-475" />
                </div>
              </div>
              {formData.partyLocation === 'other' && <div data-id="element-476">
                  <label htmlFor="otherLocationDetails" className="block text-sm font-medium text-gray-700" data-id="element-477">
                    Please explain the location
                  </label>
                  <div className="mt-1" data-id="element-478">
                    <input type="text" name="otherLocationDetails" id="otherLocationDetails" value={formData.otherLocationDetails} onChange={handleChange} required className="py-3 px-4 block w-full shadow-sm focus:ring-primary-500 focus:border-primary-500 border-gray-300 rounded-md" placeholder="Please describe the location" data-id="element-479" />
                  </div>
                </div>}
              <div className="sm:col-span-2" data-id="element-480">
                <label htmlFor="message" className="block text-sm font-medium text-gray-700" data-id="element-481">
                  Additional Details
                </label>
                <div className="mt-1" data-id="element-482">
                  <textarea id="message" name="message" rows={4} value={formData.message} onChange={handleChange} required className="py-3 px-4 block w-full shadow-sm focus:ring-primary-500 focus:border-primary-500 border border-gray-300 rounded-md" placeholder="Tell us about your event, space requirements, or any questions you have" data-id="element-483"></textarea>
                </div>
              </div>
              <div className="sm:col-span-2" data-id="element-484">
                <button type="submit" disabled={isSubmitting} className="w-full inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-primary-500 hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-75" data-id="element-485">
                  {isSubmitting ? 'Sending...' : 'BOOK NOW'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>;
};